import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { AccountingId, InvoiceOverviewDto, InvoiceStatus, InvoiceType } from '../../config/finance/invoiceConfig'
import {
    Button,
    Label,
    Link,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import useEntity from '../../hooks/useEntity'
import Loading from '../../components/Loading'
import { Stack, StackItem, Text } from '@fluentui/react'
import useUnits from '../../hooks/useUnits'
import { UnitDto } from '../../context/GlobalContext'
import {
    CheckmarkCircle16Regular,
    Circle16Regular,
    ErrorCircle16Regular,
    PersonAdd16Regular,
    ScanDash16Regular,
    Send16Regular,
    Warning24Regular,
} from '@fluentui/react-icons'
import { EntityType } from '../../v2/components/Form/Accounting/EntityField'
import MoneyText from '../Text/Currency/MoneyText'
import { useForceUpdate } from '@fluentui/react-hooks'
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { normalizeAxiosError } from '../../reducers/errorReducer'
import useAxios from '../../hooks/useAxios'
import ErrorPopover from '../Toast/ErrorPopover'
import { formatFullNumericDate } from '../../helpers/dateHelper'
import { InvoiceStatusLabels } from '../TagPicker/InvoiceStatusPicker'
import { Trans, useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import useAppToast, { AppToast } from '../../hooks/useAppToast'

type Props = {
    item: InvoiceOverviewDto
    reset: () => void
}

type SurfaceProps = Props & {
    forceUpdate: () => void
}

type Transaction = {
    status: InvoiceStatus
    currency: string
    customer: {
        code: string
        email: string
        id: string
        name: string
        reference: string
    }
    date: string
    description: string
    journal: AccountingId
    lineItems: {
        costCenter?: AccountingId
        costUnit?: AccountingId
        project?: AccountingId
        description: string
        gla?: AccountingId
        value: string
        vat?: AccountingId
        vatValue: string
        period?: AccountingId
    }[]
    name: string
    number: string
    period?: string
    term: number
    total: {
        value: string
        subTotal: string
        vatValue: string
        gla?: AccountingId
    }
    unit: string
}

const getSubmissionIcon = ({ item }: Props): ReactElement => {
    if ([InvoiceType.Split].includes(item.type)) {
        return <></>
    } else if (!item.unit) {
        return <ErrorCircle16Regular />
    } else if (!item.transaction?.code) {
        return <Circle16Regular />
    }
    return <CheckmarkCircle16Regular />
}

const listStyle = makeStyles({
    button: { minWidth: 0, marginTop: '-3px' },
    tableCell: { lineHeight: '0px' },
    alignRight: { justifyContent: 'right' },
    tableRowBorder: {
        borderTopStyle: 'solid',
        borderTopColor: tokens.colorNeutralStroke2,
        borderTopWidth: tokens.strokeWidthThin,
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralStroke2,
        borderBottomWidth: tokens.strokeWidthThin,
    },
})

export async function submitAccountingInvoice(
    item: InvoiceOverviewDto,
    translate: TFunction,
    toast: AppToast,
    axios: AxiosInstance,
    reset: () => void,
): Promise<void> {
    return toast
        .promise(
            axios.post<AccountingId>(`/finance/invoice/${item.id}/accounting-submit`, null, {
                headers: { SuppressErrors: true },
            }),
            {
                pending: translate('invoice.accounting.preview.submit.pending'),
                success: (response: AxiosResponse<AccountingId>) =>
                    translate('invoice.accounting.preview.submit.success', { number: item.number, code: response.data.code }),
                error: (reason: AxiosError) => (
                    <>
                        <Text>{translate('invoice.accounting.preview.submit.error', { number: item.number })}</Text>
                        {(reason.message.includes('504') || reason.message.includes('407')) && (
                            <Text>
                                <Trans
                                    i18nKey={'invoice.accounting.preview.submit.error_auth'}
                                    components={{
                                        authLink: <Link inline as={'a'} href={process.env.EXACT_AUTH_URL} target={'_blank'} />,
                                    }}
                                />
                            </Text>
                        )}
                        <ErrorPopover error={normalizeAxiosError(reason)} />
                    </>
                ),
            },
        )
        .then(reset)
}

function LineItemTable({ transaction, unit }: { transaction: Transaction; unit: UnitDto }): ReactElement {
    const { t: translate } = useTranslation()
    const listStyles = listStyle()
    const hasPeriod = useMemo<boolean>(() => transaction?.lineItems.filter(item => item.period).length > 0, [])

    return (
        <Table size="extra-small">
            <TableHeader>
                <TableRow className={listStyles.tableRowBorder}>
                    <TableHeaderCell>
                        <TableCellLayout truncate>{translate('invoice.accounting.preview.description.label')}</TableCellLayout>
                    </TableHeaderCell>
                    <TableHeaderCell>
                        <TableCellLayout truncate>{translate('invoice.accounting.preview.price.subtotal.label')}</TableCellLayout>
                    </TableHeaderCell>
                    <TableHeaderCell>
                        <TableCellLayout truncate>{translate('invoice.accounting.preview.vat.label')}</TableCellLayout>
                    </TableHeaderCell>
                    <TableHeaderCell>
                        <TableCellLayout truncate>{translate('invoice.accounting.preview.gla.label')}</TableCellLayout>
                    </TableHeaderCell>
                    {(!unit || unit.dimensions?.includes(EntityType.CostCenter)) && (
                        <TableHeaderCell>
                            <TableCellLayout truncate>{translate('invoice.accounting.preview.costcenter.label')}</TableCellLayout>
                        </TableHeaderCell>
                    )}
                    {(!unit || unit.dimensions?.includes(EntityType.CostUnit)) && (
                        <TableHeaderCell>
                            <TableCellLayout truncate>{translate('invoice.accounting.preview.costunit.label')}</TableCellLayout>
                        </TableHeaderCell>
                    )}
                    {(!unit || unit.dimensions?.includes(EntityType.Project)) && (
                        <TableHeaderCell>
                            <TableCellLayout truncate>{translate('invoice.accounting.preview.project.label')}</TableCellLayout>
                        </TableHeaderCell>
                    )}
                    {hasPeriod && (
                        <TableHeaderCell>
                            <TableCellLayout truncate>{translate('invoice.accounting.preview.period.label')}</TableCellLayout>
                        </TableHeaderCell>
                    )}
                </TableRow>
            </TableHeader>
            <TableBody>
                {transaction?.lineItems.map((lineItem, index) => (
                    <TableRow key={`line-item-${index}`}>
                        <TableCell>
                            <TableCellLayout truncate>{lineItem.description}</TableCellLayout>
                        </TableCell>
                        <TableCell>
                            <TableCellLayout>
                                <MoneyText value={{ amount: lineItem.value, currency: transaction?.currency }} />
                            </TableCellLayout>
                        </TableCell>
                        <TableCell>
                            <TableCellLayout>
                                <MoneyText
                                    value={{ amount: lineItem.vatValue, currency: transaction?.currency }}
                                    tooltip={lineItem.vat?.code + ' - ' + lineItem.vat?.name}
                                />
                            </TableCellLayout>
                        </TableCell>
                        <TableCell>
                            <TableCellLayout>
                                <Tooltip
                                    relationship={'label'}
                                    content={lineItem.gla?.name ?? translate('invoice.accounting.preview.gla.empty')}
                                    withArrow
                                >
                                    <Label>{lineItem.gla?.code ?? <Warning24Regular />}</Label>
                                </Tooltip>
                            </TableCellLayout>
                        </TableCell>
                        {(!unit || unit.dimensions?.includes(EntityType.CostCenter)) && (
                            <TableCell>
                                <TableCellLayout className={listStyles.tableCell}>
                                    <Tooltip
                                        relationship={'label'}
                                        content={lineItem.costCenter?.name ?? translate('invoice.accounting.preview.costcenter.empty')}
                                        withArrow
                                    >
                                        <Label>{lineItem.costCenter?.code ?? <Warning24Regular />}</Label>
                                    </Tooltip>
                                </TableCellLayout>
                            </TableCell>
                        )}
                        {(!unit || unit.dimensions?.includes(EntityType.CostUnit)) && (
                            <TableCell>
                                <TableCellLayout className={listStyles.tableCell}>
                                    <Tooltip
                                        relationship={'label'}
                                        content={lineItem.costUnit?.name ?? translate('invoice.accounting.preview.costunit.empty')}
                                        withArrow
                                    >
                                        <Label>{lineItem.costUnit?.code ? lineItem.costUnit?.code : <Warning24Regular />}</Label>
                                    </Tooltip>
                                </TableCellLayout>
                            </TableCell>
                        )}
                        {(!unit || unit.dimensions?.includes(EntityType.Project)) && (
                            <TableCell>
                                <TableCellLayout className={listStyles.tableCell}>
                                    <Tooltip
                                        relationship={'label'}
                                        content={lineItem.project?.name ?? translate('invoice.accounting.preview.project.empty')}
                                        withArrow
                                    >
                                        <Label>{lineItem.project?.code ?? <Warning24Regular />}</Label>
                                    </Tooltip>
                                </TableCellLayout>
                            </TableCell>
                        )}
                        {hasPeriod && (
                            <TableCell>
                                <TableCellLayout className={listStyles.tableCell}>
                                    <Tooltip
                                        relationship={'label'}
                                        content={lineItem.period?.name ?? translate('invoice.accounting.preview.period.empty')}
                                        withArrow
                                    >
                                        <Label>{lineItem.period?.code ?? <ScanDash16Regular />}</Label>
                                    </Tooltip>
                                </TableCellLayout>
                            </TableCell>
                        )}
                    </TableRow>
                ))}
                <TableRow key={'line-item-total'} className={listStyles.tableRowBorder}>
                    <TableCell>
                        <TableCellLayout truncate>{translate('invoice.accounting.preview.price.total.label')}</TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>
                            <MoneyText value={{ amount: transaction?.total.subTotal, currency: transaction?.currency }} />
                        </TableCellLayout>
                    </TableCell>
                    <TableCell>
                        <TableCellLayout>
                            <MoneyText value={{ amount: transaction?.total.vatValue, currency: transaction?.currency }} />
                        </TableCellLayout>
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    )
}

function Surface({ item, reset, forceUpdate }: SurfaceProps): ReactElement {
    const axios = useAxios()
    const toast = useAppToast()
    const { t: translate } = useTranslation()

    // Use all units in case we are processing older data
    const { units } = useUnits()
    const [{ data: transaction, loading }] = useEntity<Transaction>(id => ({ url: `/finance/invoice/${id}/accounting-submit` }), item.id)
    const unit = useMemo<UnitDto>(() => units.find(unit => unit.key === transaction?.unit), [units, transaction?.unit])

    useEffect(forceUpdate, [transaction])

    if (loading) {
        return <Loading />
    }

    return (
        <Stack tokens={{ childrenGap: 's1' }} styles={{ root: { maxWidth: '720px' } }}>
            <Stack horizontal tokens={{ childrenGap: 'm' }}>
                <StackItem grow>
                    <Text variant="large">
                        {transaction?.number} - {transaction?.name}
                    </Text>
                </StackItem>
                {![InvoiceType.Split].includes(item.type) &&
                    ![InvoiceStatus.New, InvoiceStatus.Draft].includes(item.status) &&
                    item.unit &&
                    !item.transaction && (
                        <StackItem>
                            <Button
                                appearance="primary"
                                icon={<Send16Regular />}
                                onClick={() => submitAccountingInvoice(item, translate, toast, axios, reset)}
                            >
                                {translate('invoice.accounting.preview.submit.label')}
                            </Button>
                        </StackItem>
                    )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 'm' }}>
                <Stack>
                    <Label weight="semibold">{translate('invoice.accounting.preview.submit_status.label')}</Label>
                    <Text>
                        {translate(
                            item.transaction?.code
                                ? 'invoice.accounting.preview.submit_status.submitted'
                                : 'invoice.accounting.preview.submit_status.not_submitted',
                        )}
                    </Text>
                </Stack>
                {item.transaction && (
                    <Stack>
                        <Label weight="semibold">{translate('invoice.accounting.preview.transaction.label')}</Label>
                        <Text>{item.transaction.code}</Text>
                    </Stack>
                )}
                {transaction?.status && (
                    <Stack>
                        <Label weight="semibold">{translate('invoice.accounting.preview.status.label')}</Label>
                        <Text>
                            {translate(InvoiceStatusLabels.get(transaction?.status ? transaction?.status : InvoiceStatus.Draft).name, { count: 0 })}
                        </Text>
                    </Stack>
                )}
            </Stack>
            {units.length > 1 && (
                <Stack grow>
                    <Label weight="semibold">{translate('invoice.accounting.preview.unit.label')}</Label>
                    {unit ? (
                        <Text>
                            {unit.name} ({translate(`accounting.type.${unit.accounting.toLowerCase()}`)})
                        </Text>
                    ) : (
                        <Warning24Regular />
                    )}
                </Stack>
            )}
            <Stack>
                <Label weight="semibold">{translate('invoice.accounting.preview.description.label')}</Label>
                <Text>{transaction?.description}</Text>
            </Stack>
            <Stack>
                <Label weight="semibold">{translate('invoice.accounting.preview.debtor.label')}</Label>
                <Stack horizontal tokens={{ childrenGap: 'm' }} verticalAlign="center">
                    {transaction?.customer?.code ? <Text>{transaction?.customer?.code}</Text> : <PersonAdd16Regular />}
                    <Stack>
                        <Text>{transaction?.customer.name}</Text>
                        <Text>{transaction?.customer.email}</Text>
                    </Stack>
                </Stack>
            </Stack>
            <Stack grow horizontal tokens={{ childrenGap: 'm' }}>
                <Stack grow>
                    <Label weight="semibold">{translate('invoice.accounting.preview.date.label')}</Label>
                    <Text>{formatFullNumericDate(transaction?.date)}</Text>
                </Stack>
                <Stack grow>
                    <Label weight="semibold">{translate('invoice.accounting.preview.term.label')}</Label>
                    <Text>
                        {transaction?.term} {translate('invoice.accounting.preview.term.days')}
                    </Text>
                </Stack>
                {transaction?.period && (
                    <Stack grow>
                        <Label weight="semibold">{translate('invoice.accounting.preview.invoiceperiod.label')}</Label>
                        <Text>{transaction?.period}</Text>
                    </Stack>
                )}
                <Stack grow>
                    <Label weight="semibold">{translate('invoice.accounting.preview.journal.label')}</Label>
                    {transaction?.journal ? <Text>{transaction?.journal?.code}</Text> : <Warning24Regular />}
                </Stack>
                <Stack grow>
                    <Label weight="semibold">{translate('invoice.accounting.preview.price.total.label')}</Label>
                    <MoneyText value={{ amount: transaction?.total.value, currency: transaction?.currency }} />
                </Stack>
            </Stack>
            <Stack>
                <Label weight="semibold">{translate('invoice.accounting.preview.lines.label')}</Label>
                <LineItemTable transaction={transaction} unit={unit} />
            </Stack>
        </Stack>
    )
}

export default function AccountingSubmissionPreview(props: Props): ReactElement {
    const forceUpdate = useForceUpdate()
    return (
        <Popover withArrow>
            <PopoverTrigger>
                <Button appearance="transparent" icon={getSubmissionIcon(props)} size="small" />
            </PopoverTrigger>
            <PopoverSurface>
                <Surface {...props} forceUpdate={forceUpdate} />
            </PopoverSurface>
        </Popover>
    )
}
